import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FileText, MessageSquare, Send, Download, Trash2, Plus, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import API from '../../services/api';

const Governance = () => {
  const [documents, setDocuments] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const [selectedDocument, setSelectedDocument] = useState(null);


  const { session } = useAuth();
  const sessionId = session?.user?.id || 'guest';
  const activeSessionId = useRef(sessionId);

  useEffect(() => {
    activeSessionId.current = sessionId;
    return () => { activeSessionId.current = null; };
  }, [sessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---------------- Documents ----------------
  const fetchDocuments = useCallback(async (signal) => {
    if (activeSessionId.current !== sessionId) return;
    try {
      const response = await API.get(`documents/`, { params: { session_id: sessionId }, signal });
      if (!signal?.aborted && activeSessionId.current === sessionId) {
        setDocuments(response.data);
      }
    } catch (error) {
      if (!signal?.aborted) console.error('Error fetching documents:', error);
    }
  }, [sessionId]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('session_id', sessionId);

    setIsUploading(true);
    try {
      await API.post('documents/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchDocuments();
      fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await API.delete('documents/delete/', {
        data: { document_id: documentId, session_id: sessionId },
      });
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document.');
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      const response = await API.get(`documents/${document.id}/download/`, {
        params: { session_id: sessionId },
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = document.title;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    const iconClass = "w-5 h-5";
    switch (fileType) {
      case 'pdf': return <FileText className={`${iconClass} text-red-400`} />;
      case 'docx': return <FileText className={`${iconClass} text-blue-400`} />;
      case 'txt': case 'md': return <FileText className={`${iconClass} text-green-400`} />;
      default: return <FileText className={`${iconClass} text-gray-400`} />;
    }
  };

  // ---------------- Chat ----------------
  const fetchChatSessions = useCallback(async (signal) => {
    if (activeSessionId.current !== sessionId) return;
    try {
      const response = await API.get(`chat_sessions/`, { params: { session_id: sessionId }, signal });
      if (!signal?.aborted && activeSessionId.current === sessionId) {
        setChatSessions(response.data);
      }
    } catch (error) {
      if (!signal?.aborted) console.error('Error fetching chat sessions:', error);
    }
  }, [sessionId]);

  useEffect(() => {
    const controller = new AbortController();
    setDocuments([]);
    setChatSessions([]);
    setCurrentSession(null);
    setMessages([]);
    setSelectedDocument(null);
    fetchDocuments(controller.signal);
    fetchChatSessions(controller.signal);
    return () => controller.abort();
  }, [fetchDocuments, fetchChatSessions]);

  const loadChatSession = async (session) => {
    setCurrentSession(session);
    try {
      const response = await API.get(`chat_sessions/${session.id}/messages/`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    }
  };

  const startNewChat = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSendingMessage) return;

    const userMessage = { role: 'user', content: inputMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsSendingMessage(true);


    try {
      const response = await API.post('chat/', 
        {
          message: inputMessage,
          session_id: sessionId,
          current_session_id: currentSession?.id,
          document_id: currentSession?.document_id || selectedDocument?.id,
        }
      );

      const data = response.data;
      const assistantMessage = { role: 'assistant', content: data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, assistantMessage]);

      if (!currentSession || currentSession.id !== data.session_id) {
        setCurrentSession({ id: data.session_id });
        fetchChatSessions();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Error occurred. Please try again.', timestamp: new Date().toISOString(), error: true }
      ]);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };


  const handleSelectDocument = async (doc) => {
    setSelectedDocument(doc); // <— this is key

    try {
      // check if chat session already exists for this document
      const session = chatSessions.find(s => s.document === doc.id);

      if (session) {
        await loadChatSession(session);
        setCurrentSession({ ...session, document_id: doc.id });
      } else {
        // if no chat session exists yet, start a new one tied to the document
        setCurrentSession({ document_id: doc.id });
        setMessages([]);
      }
    } catch (error) {
      console.error('Error selecting document chat:', error);
    }
  };



  // ---------------- Render ----------------
  return (
    <div className="h-full bg-transparent flex">
      {/* Left Sidebar - Documents */}
      <div className="w-96 border-r border-slate-700/50 flex flex-col bg-transparent backdrop-blur-sm">
        {/* Header */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-white">Documents</h1>
              <p className="text-sm text-slate-400">{documents.length} files uploaded</p>
            </div>
          </div>

          {/* Upload Button */}
          <input 
            ref={fileInputRef} 
            type="file" 
            accept=".pdf,.docx,.txt,.md" 
            onChange={handleFileUpload} 
            className="hidden" 
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {isUploading ? 'Uploading...' : 'Upload Documents'}
          </button>
          <p className="text-xs text-slate-500 mt-3 text-center">
            PDF, DOCX, TXT, MD supported
          </p>
        </div>

        {/* Documents List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-600" />
              </div>
              <p className="text-slate-400 text-sm">No documents yet</p>
              <p className="text-slate-600 text-xs mt-1">Upload files to get started</p>
            </div>
          ) : (
            documents.map(doc => (
              <div 
                key={doc.id} 
                onClick={() => handleSelectDocument(doc)}
                className={`group bg-transparent hover:bg-slate-800 border border-slate-700/50 rounded-xl p-4 transition-all cursor-pointer ${
                  selectedDocument?.id === doc.id ? 'ring-2 ring-blue-500/50' : ''
                }`}              
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-transparent rounded-lg">
                    {getFileIcon(doc.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm truncate mb-1">
                      {doc.title}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {doc.file_type.toUpperCase()} • {formatFileSize(doc.file_size)}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleDownloadDocument(doc)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-slate-400 hover:text-white" />
                    </button>
                    <button 
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Side - Chat */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="h-20 border-b border-slate-700/50 px-8 flex items-center justify-between bg-slate-900/30 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">AI Assistant</h2>
              <p className="text-xs text-slate-400">Ask anything about your documents</p>
            </div>
          </div>
          <button
            onClick={startNewChat}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-medium transition-all flex items-center gap-2 border border-slate-700"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-8">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mb-6">
                <MessageSquare className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">
                Start a Conversation
              </h3>
              <p className="text-slate-400 mb-8 max-w-md">
                Upload documents and ask questions. I'll help you understand, analyze, and extract insights from your files.
              </p>
              {/* <div className="grid grid-cols-2 gap-3 w-full max-w-xl">
                <button className="p-4 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl text-left transition-all">
                  <p className="text-sm font-medium text-white mb-1">Summarize documents</p>
                  <p className="text-xs text-slate-500">Get key points from your files</p>
                </button>
                <button className="p-4 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl text-left transition-all">
                  <p className="text-sm font-medium text-white mb-1">Find information</p>
                  <p className="text-xs text-slate-500">Search across all documents</p>
                </button>
                <button className="p-4 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl text-left transition-all">
                  <p className="text-sm font-medium text-white mb-1">Compare documents</p>
                  <p className="text-xs text-slate-500">Analyze differences and similarities</p>
                </button>
                <button className="p-4 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl text-left transition-all">
                  <p className="text-sm font-medium text-white mb-1">Extract data</p>
                  <p className="text-xs text-slate-500">Pull specific information</p>
                </button>
              </div> */}
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-2xl ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                    <div className={`rounded-2xl p-4 ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : msg.error
                          ? 'bg-red-500/10 text-red-200 border border-red-500/20'
                          : 'bg-slate-800/50 text-white border border-slate-700/50'
                    }`}>
                      <p className="text-md leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <div className={`text-xs text-slate-500 mt-2 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                      {formatTimestamp(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
              {isSendingMessage && (
                <div className="flex justify-start">
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4">
                    <div className="flex gap-2">
                      <div className="w-2 h-2 bg-slate-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-slate-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-slate-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-slate-700/50 p-6 bg-slate-900/30 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3 items-end">
              <div className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 focus-within:border-blue-500/50 transition-all">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask me anything about your documents..."
                  disabled={isSendingMessage}
                  rows="1"
                  className="w-full bg-transparent text-white placeholder-slate-500 text-md resize-none focus:outline-none"
                  style={{ maxHeight: '120px' }}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={isSendingMessage || !inputMessage.trim()}
                className="p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-3 text-center">
              Press Enter to send, Shift + Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Governance;
